import React, { useCallback, useEffect, useRef, useState } from 'react';
import posthog from 'posthog-js';
import { Button, Dialog, Popup } from 'antd-mobile';
import { useLatest, useMemoizedFn, useMount, useUnmount } from 'ahooks';
import { IConsultationSkip, TrackerCustomEvent } from 'common-package';
import { useSelector } from 'react-redux';
import { Icon } from '@fuxi/eevee-icon';
import { selectDevicePlatform } from '@/store/device';
import service from '@/service';
import { ScrollView, Toast, When } from '@/components';
import { useCustomCamera } from '@/hooks';
import { storagePrefixV2 } from '@/constant';
import { ShowTitle } from '../show-title';
import {
  setCurrentStep,
  setFormValue,
  setNextBtnConfig,
  setPrevBtnConfig,
  useAIDiagnoseStore,
} from '../../store/useAIDiagnoseStore';
import { SYMPTOMATIC_2_QUESTION } from './config';

import cx from './index.module.scss';

interface Props {}

const CAMER_MARKER_URL = storagePrefixV2 + '/ai-consulation/tongue-marker.png';

export const TongueDiagnosis: React.FC<Props> = () => {
  const [picUrl, setPicUrl] = useState<string>('');
  const devicePlatform = useSelector(selectDevicePlatform);
  const { userInfo } = useAIDiagnoseStore.use.formValue();
  const currentUserGender = useLatest(userInfo?.gender);
  const latestDevicePlatform = useLatest(devicePlatform);
  const [isShowTips, setIsShowTips] = useState(false);
  const [userConditionList, setUserConditionList] = useState<GetTongueResultResponse['inquiry_questions']>([]);
  const [isInConfirmStep, setIsConfirmStep] = useState(false);
  const [confirmSelectIdxs, setConfirmSelectIdxs] = useState<Record<number, 0 | 1>>({});
  const currentApiSessionId = useRef(null);
  const [isConfirmSelectLoading, setIsConfirmSelectLoading] = useState(false);

  const { handleScan } = useCustomCamera({
    buttonPosition: 'left',
    shouldTransFromFrontImage: true,
  });

  const hanldeTakePhoto = useCallback(async () => {
    if (latestDevicePlatform.current !== 'android') {
      Toast.showVScreen('该功能仅可在Pad上使用');
      return;
    }

    const scanData = await handleScan({
      width: 270,
      url: CAMER_MARKER_URL,
    });
    setPicUrl(scanData?.url);

    Toast.loadingVScreen('正在获取舌诊结果，请稍后', {
      duration: 200000,
      maskClickable: false,
    });

    const { data } = await service.AIDiagnose.getTongueResult({
      imageUrl: scanData?.url,
      gender: currentUserGender.current,
    });

    currentApiSessionId.current = data?.session_id;

    if (!data) {
      setPicUrl('');
      Toast.warnVScreen('舌诊检测失败，您可以再拍摄一次');
    } else {
      Toast.clear();
      setIsConfirmStep(true);
      setUserConditionList(data?.inquiry_questions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleScan]);

  const onConfirmSelect = useMemoizedFn(async () => {
    try {
      setIsConfirmSelectLoading(true);
      const selectConditonIdxs = Object.entries(confirmSelectIdxs)
        ?.filter(item => !!item?.[1])
        ?.map(item => item?.[0]);

      const selectConditionList = userConditionList?.filter((item, index) =>
        selectConditonIdxs?.map(item => +item)?.includes(index)
      );

      const { data } = await service.AIDiagnose.getTongueReport({
        session_id: currentApiSessionId.current,
        answers: selectConditionList,
      });

      setFormValue({ tongueDiagnoseReportId: { value: data } }, { isMerge: true });
      setCurrentStep(prev => ({ currentStep: prev + 1 }));
    } finally {
      setIsConfirmSelectLoading(false);
    }
  });

  useMount(() => {
    setIsShowTips(true);
  });

  useUnmount(() => {
    Toast.clear();
  });

  useEffect(() => {
    setPrevBtnConfig({
      show: true,
      disable: !!picUrl,
      text: picUrl ? '再拍一次' : '上一步',
      action: () => {
        if (picUrl) {
          hanldeTakePhoto();
        } else {
          setCurrentStep(prev => ({ currentStep: prev - 1 }));
          setNextBtnConfig({ text: '下一步' });
        }
      },
    });
    setNextBtnConfig({
      text: picUrl ? '提交' : '跳过',
      disable: !!picUrl,
      action: () => {
        if (!picUrl) {
          Dialog.clear();
          Dialog.confirm({
            className: 'v-screen-dialog',
            title: '确认要跳过舌诊吗？',
            content: '跳过舌诊，将无法检测您的体质类型哦',
            confirmText: '确认',
            onConfirm: async () => {
              posthog.capture(TrackerCustomEvent.AiConsultationSkipClk, {
                et: 'CLK',
                c1: IConsultationSkip.tongueDiagnose,
              });
              setCurrentStep(prev => ({ currentStep: prev + 1 }));
            },
          });
        } else {
          setCurrentStep(prev => ({ currentStep: prev + 1 }));
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picUrl]);

  useEffect(() => {
    if (isInConfirmStep) {
      setPrevBtnConfig({
        show: true,
        text: '跳过',
        disable: false,
        action: () => {
          Dialog.confirm({
            className: 'v-screen-dialog',
            title: '确认要跳过问答题吗？',
            content: '跳过问答题，可能会影响您的体质检测结果',
            confirmText: '确认',
            onConfirm: async () => {
              const { data } = await service.AIDiagnose.getTongueReport({
                session_id: currentApiSessionId.current,
                answers: [],
              });

              posthog.capture(TrackerCustomEvent.AiConsultationSkipClk, {
                et: 'CLK',
                c1: IConsultationSkip.issueConfirm,
              });

              setFormValue({ tongueDiagnoseReportId: { value: data } }, { isMerge: true });
              setCurrentStep(prev => ({ currentStep: prev + 1 }));
            },
          });
        },
      });
      setNextBtnConfig({
        text: '完成',
        loading: isConfirmSelectLoading,
        disable: Object.keys(confirmSelectIdxs)?.length !== userConditionList?.length,
        action: onConfirmSelect,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInConfirmStep, isConfirmSelectLoading, userConditionList, confirmSelectIdxs]);

  return (
    <div className={cx('TongueDiagnosis')}>
      <ShowTitle
        title={isInConfirmStep ? '症状确认' : '舌诊'}
        subTitle={
          isInConfirmStep
            ? '再回答几个问题，能让舌诊检测结果更为精准哦'
            : '了解舌头的状态，可以更准确的判断您的身体问题哦'
        }
      />
      <When condition={!isInConfirmStep}>
        <div className={cx('camera-tips')} onClick={() => setIsShowTips(true)}>
          <span className={cx('tip-text')}>舌诊拍照注意事项</span>
          <Icon name="arrow-right" />
        </div>
      </When>
      <div className={cx('content')}>
        {isInConfirmStep ? (
          <ScrollView autoRefresh scrollY className={cx('confirm-form-wrapper')}>
            <div className={cx('confirm-form-box')}>
              {userConditionList?.map((item, index) => (
                <div key={item?.value} className={cx('form-item')}>
                  <div className={cx('label')}>
                    {SYMPTOMATIC_2_QUESTION[item?.name] || `您是否有${item.name}的情况？`}
                  </div>
                  <div className={cx('radio-box')}>
                    <div
                      className={cx('radio-item', { active: confirmSelectIdxs[index] === 1 })}
                      onClick={() =>
                        setConfirmSelectIdxs(prev => ({
                          ...prev,
                          [index]: 1,
                        }))
                      }
                    >
                      是
                    </div>
                    <div
                      className={cx('radio-item', { active: confirmSelectIdxs[index] === 0 })}
                      onClick={() =>
                        setConfirmSelectIdxs(prev => ({
                          ...prev,
                          [index]: 0,
                        }))
                      }
                    >
                      否
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollView>
        ) : (
          <>
            <div className={cx('tip-img')}>
              <div className={cx('mirror-bg')} />
              <div className={cx('img-box')}>
                <img src={storagePrefixV2 + '/tongue-tips.png'} alt="" />
              </div>
              <div className={cx('tongue-tip')}>
                <Icon className={cx('icon')} name="info-circle" />
                <span>舌诊示例</span>
              </div>
            </div>
            <div className={cx('camera-wrapper', { 'has-pic': !!picUrl })} onClick={() => !picUrl && hanldeTakePhoto()}>
              {picUrl ? (
                <div className={cx('pic-wrapper')}>
                  <img src={picUrl} alt="" />
                </div>
              ) : (
                <>
                  <Icon name="camera-pure-color" className={cx('camera-icon')} />
                  <div className={cx('camera-tip-text')}>
                    请在自然光下，口张开、放松舌头并平展舌面，
                    <br />
                    根据提示将舌头正面对准口型框，进行拍照
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
      <Popup
        getContainer={null}
        bodyClassName={cx('tongue-diagnosis-tips-box')}
        visible={isShowTips}
        destroyOnClose={true}
        onClose={() => {
          setIsShowTips(false);
        }}
      >
        <div className={cx('tongue-diagnosis-tips-header')}>舌诊拍照注意事项</div>
        <div className={cx('tongue-diagnosis-tips-content')}>
          <div className={cx('item')}>
            <div className={cx('item-header')}>1. 请保持正确的拍照姿势与距离</div>
            <div className={cx('item-content')}>
              要尽量张开口，自然舒展地将舌伸出口外，充分暴露，使舌体放松，向两侧展平，舌尖向下垂请将舌头保持在拍摄指导的轮廓里，不要距距离过远或偏离。
            </div>
            <div className={cx('img-box')}>
              <img src={storagePrefixV2 + '/asdasdf.png'} alt="" />
            </div>
          </div>
          <div className={cx('item')}>
            <div className={cx('item-header')}>2. 请在光线条件良好的下拍摄</div>
            <div className={cx('item-content')}>
              以白天充足而柔和的光线为好，应注意避开有色門窗和周围反光较强的有色物体，且不要在昏暗光线下拍摄。
            </div>
            <div className={cx('img-box')}>
              <img src={storagePrefixV2 + '/qwfwqg.png'} alt="" />
            </div>
          </div>
          <div className={cx('item')}>
            <div className={cx('item-header')}>3. 如果您的舌头因为饮食染色，也会影响拍摄结果的准确性哦</div>
          </div>
        </div>
        <div className={cx('tongue-diagnosis-tips-footer')}>
          <Button color="primary" block className={cx('btn')} onClick={() => setIsShowTips(false)}>
            我知道了
          </Button>
        </div>
      </Popup>
    </div>
  );
};
